export const ADMINISTRATOR_ROLE = 'administrator';
export const SITE_MANAGER_ROLE = 'site_manager';
export const LEADER_ROLE = 'leader';
export const RESEARCHER_ROLE = 'researcher';
export const TEACHER_ROLE = 'teacher';
export const TEACHING_ASSISTANT_ROLE = 'teaching_assistant';

export interface RoleAccess {
  autoReporter: boolean;
  confirmatory: boolean;
  managementHome: boolean;
  managementApplications: boolean;
}

/**
 * Access by Drupal role. Read a role to see every app surface it can enter.
 * Site Manager is a header pill only and is not in this map.
 */
export const ROLE_ACCESS: { [role: string]: RoleAccess } = {
  [TEACHING_ASSISTANT_ROLE]: {
    autoReporter: true,
    confirmatory: false,
    managementHome: false,
    managementApplications: false
  },
  [TEACHER_ROLE]: {
    autoReporter: true,
    confirmatory: false,
    managementHome: false,
    managementApplications: false
  },
  [RESEARCHER_ROLE]: {
    autoReporter: true,
    confirmatory: true,
    managementHome: true,
    managementApplications: false
  },
  [LEADER_ROLE]: {
    autoReporter: true,
    confirmatory: true,
    managementHome: true,
    managementApplications: true
  },
  [ADMINISTRATOR_ROLE]: {
    autoReporter: true,
    confirmatory: true,
    managementHome: true,
    managementApplications: true
  }
};

function rolesFor(access: keyof RoleAccess): string[] {
  return Object.keys(ROLE_ACCESS).filter(role => ROLE_ACCESS[role][access]);
}

export const AUTO_REPORTER_ROLES: string[] = rolesFor('autoReporter');
export const CONFIRMATORY_ROLES: string[] = rolesFor('confirmatory');
export const MANAGEMENT_HOME_ROLES: string[] = rolesFor('managementHome');
export const MANAGEMENT_APPLICATIONS_ROLES: string[] = rolesFor('managementApplications');

export interface RolePill {
  role: string;
  label: string;
  tagClass: string;
}

/** Header pills for known Drupal roles. Visibility only; access is ROLE_ACCESS. */
export const ROLE_PILLS: { [role: string]: { label: string; tagClass: string } } = {
  [TEACHING_ASSISTANT_ROLE]: { label: 'Teaching assistant', tagClass: 'is-info' },
  [TEACHER_ROLE]: { label: 'Teacher', tagClass: 'is-success' },
  [RESEARCHER_ROLE]: { label: 'Researcher', tagClass: 'is-link' },
  [LEADER_ROLE]: { label: 'Leader', tagClass: 'is-warning' },
  [SITE_MANAGER_ROLE]: { label: 'Site Manager', tagClass: 'is-primary' },
  [ADMINISTRATOR_ROLE]: { label: 'Administrator', tagClass: 'is-dark' }
};

export const ROLE_PILL_ORDER: string[] = [
  TEACHING_ASSISTANT_ROLE,
  TEACHER_ROLE,
  RESEARCHER_ROLE,
  LEADER_ROLE,
  SITE_MANAGER_ROLE,
  ADMINISTRATOR_ROLE
];

export function pillsForRoles(roles: string[] | null | undefined): RolePill[] {
  if (!roles || roles.length === 0) {
    return [];
  }

  const owned: { [role: string]: boolean } = {};
  for (let i = 0; i < roles.length; i++) {
    owned[String(roles[i]).toLowerCase()] = true;
  }

  return ROLE_PILL_ORDER
    .filter(role => owned[role])
    .map(role => ({
      role,
      label: ROLE_PILLS[role].label,
      tagClass: ROLE_PILLS[role].tagClass
    }));
}
